import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormikProps, useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { ApiPairs } from 'api';
import type { IPairWallet } from 'api/apiPairs/models';
import { useTypedSelector } from 'store';
import { bn, bnFrom, humanizeBn } from 'tools/math';
import { EDexBot, IDextSLOrganicVolumeTask } from 'types/bots';
import { durationToMs, msToDuration } from 'utils/duration';

import { validationSchema } from './validationSchema';

interface IForm {
  enabled: boolean;
  uniq_id: string;
  start_at: number | null;
  end_at: number | null;
  min_pause_between_series: number | undefined;
  max_pause_between_series: number | undefined;
  min_volume: string;
  max_volume: string;
  batch_wallets_min: string;
  batch_wallets_max: string;
}

interface IUseOrganicTaskProps {
  onSubmit: (task: IDextSLOrganicVolumeTask) => void;
  onClose: () => void;
  task?: IDextSLOrganicVolumeTask;
  mode: 'create' | 'edit';
}

const slBotId = Number(EDexBot.sl_bot);

const getAllSlWallets = async (pairId: number): Promise<IPairWallet[]> => {
  const wallets: IPairWallet[] = [];
  let lastSeenId: number | undefined;

  for (;;) {
    const response = await ApiPairs.getWalletsConnectedToPair({
      pairId,
      limit: 1000,
      lastSeenId,
      bots_ids: [String(slBotId)],
    });
    if (!response.isSuccess) throw new Error(response.errorMessage);

    wallets.push(...response.data.items);
    if (!response.data.has_next) return wallets;

    const nextLastSeenId = response.data.items.at(-1)?.id;
    if (!nextLastSeenId || nextLastSeenId === lastSeenId) return wallets;
    lastSeenId = nextLastSeenId;
  }
};

const useOrganicTask = ({ mode, onClose, onSubmit: submit, task }: IUseOrganicTaskProps) => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const [edited, setEdited] = useState<boolean>(false);

  const slWalletsQuery = useQuery({
    queryKey: ['dex-sl-organic-wallets', dexPair.id],
    queryFn: () => getAllSlWallets(dexPair.id),
    refetchOnWindowFocus: false,
  });
  const slWallets = slWalletsQuery.data ?? [];
  const poolSize = slWallets.length;

  const initialValues = useMemo<IForm>(() => {
    if (mode === 'edit' && task) {
      return {
        enabled: task.enabled,
        uniq_id: task.uniq_id,
        start_at: dayjs(task.start_at).valueOf(),
        end_at: null,
        min_pause_between_series: durationToMs(task.options.min_pause_between_series),
        max_pause_between_series: durationToMs(task.options.max_pause_between_series),
        min_volume: humanizeBn(bnFrom(task.options.min_volume), dexPair.token_base.decimals),
        max_volume: humanizeBn(bnFrom(task.options.max_volume), dexPair.token_base.decimals),
        batch_wallets_min: String(task.options.batch_wallets_min || 1),
        batch_wallets_max: String(
          task.options.batch_wallets_max || task.options.batch_wallets_min || 1,
        ),
      };
    }

    return {
      uniq_id: uuid(),
      enabled: true,
      start_at: Date.now(),
      end_at: null,
      min_pause_between_series: 12_000,
      max_pause_between_series: 20_000,
      min_volume: '',
      max_volume: '',
      batch_wallets_min: '1',
      batch_wallets_max: String(Math.max(1, Math.min(10, poolSize || 1))),
    };
  }, [mode, task, dexPair, poolSize]);

  const onSubmit = useCallback(
    (vals: IForm) => {
      const wMin = Math.max(1, Number(vals.batch_wallets_min) || 1);
      const wMax = Math.max(wMin, Number(vals.batch_wallets_max) || wMin);
      const txsMin = wMin * 2;
      const txsMax = wMax * 2;
      const minVol = Number(vals.min_volume) || 0;
      const minTx = minVol > 0 ? String(minVol / Math.max(txsMax, 2)) : vals.min_volume;

      submit({
        enabled: vals.enabled,
        end_at: null,
        start_at: dayjs(vals.start_at).toISOString(),
        uniq_id: vals.uniq_id,
        options: {
          close_bar_before: '1s',
          indicator_period: 1,
          time_frame: '1m',
          min_pause_between_series: msToDuration(vals.min_pause_between_series),
          max_pause_between_series: msToDuration(vals.max_pause_between_series),
          min_volume: bn(vals.min_volume, dexPair.token_base.decimals).toString(),
          max_volume: bn(vals.max_volume, dexPair.token_base.decimals).toString(),
          max_transactions_in_series: txsMax,
          min_transactions_in_series: txsMin,
          min_transaction_amount_base: bn(minTx, dexPair.token_base.decimals).toString(),
          per_transaction_amount_dev: 0.03,
          stop_volume_threshold_percentage: 0.2,
          wallet_count: poolSize,
          wallet_addresses: slWallets.map(w => w.address).filter(Boolean),
          batch_wallets_min: wMin,
          batch_wallets_max: wMax,
        },
      } as IDextSLOrganicVolumeTask);
      onClose();
    },
    [onClose, submit, dexPair, poolSize, slWallets],
  );

  const { handleSubmit, setFieldValue, values, errors, touched }: FormikProps<IForm> =
    useFormik<IForm>({
      initialValues,
      onSubmit: onSubmit,
      validationSchema,
      enableReinitialize: true,
    });

  const handleSetFieldValue = useCallback(
    (field: keyof IForm, value: any) => {
      setEdited(true);

      setFieldValue(field, value);
    },
    [setFieldValue],
  );

  return {
    edited,
    values,
    handleSubmit,
    setFieldValue: handleSetFieldValue,
    errors,
    touched,
    poolSize,
  };
};

export { useOrganicTask };
